import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { selectSessionAdvisor } from '@client/model';
import { inviteApi } from '@client/features/invite-client';

export const DEFAULT_REMINDER_EMAIL_BODY = `I wanted to follow up on the long-term care planning questionnaire I sent earlier. Completing this brief form (about 5 minutes) will allow me to generate personalized projections for your future care needs.

Once you've completed it, I'll be able to show you:
• Your projected long-term care costs
• A personalized care plan based on your preferences
• Different funding options we could explore together

Please let me know if you have any questions or need help accessing the form.`;

export interface SendReminderConfig {
  email: string;
  firstName: string;
  lastName: string;
  emailBody: string;
  clientId: string;
  intakeFormUrl: string;
  surveyId: string;
}

export interface SendReminderStoreType {
  reminderConfig: SendReminderConfig;
  isOpen: boolean;
  isLoading: boolean;
  isEmailValid: boolean;
  canSend: boolean;

  setReminderConfig: Dispatch<SetStateAction<SendReminderConfig>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;

  handleConfigChange: (field: keyof SendReminderConfig, value: string) => void;
  handleSendReminder: () => void;
  handleClose: () => void;
  openWithClient: (client: Client) => void;
}

export const SendReminderContext = createContext<SendReminderStoreType | null>(null);

export function useSendReminderStore() {
  const context = useContext(SendReminderContext);

  if (!context) {
    throw new Error('`useSendReminderStore` should be used inside the `<SendReminderProvider />`');
  }

  return context;
}

const isValidEmailFormat = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const initialConfig: SendReminderConfig = {
  email: '',
  firstName: '',
  lastName: '',
  emailBody: DEFAULT_REMINDER_EMAIL_BODY,
  clientId: '',
  intakeFormUrl: '',
  surveyId: '',
};

export function SendReminderProvider({ children }: { children: ReactNode }) {
  const [reminderConfig, setReminderConfig] = useState<SendReminderConfig>(initialConfig);
  const [isOpen, setIsOpen] = useState(false);

  const advisor = useSelector(selectSessionAdvisor);

  const isEmailValid = isValidEmailFormat(reminderConfig.email);
  const canSend =
    isEmailValid && reminderConfig.email.length > 0 && reminderConfig.intakeFormUrl.length > 0;

  const handleConfigChange = useCallback((field: keyof SendReminderConfig, value: string) => {
    setReminderConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setReminderConfig(initialConfig);
  }, []);

  const reminderMutation = useMutation({
    mutationFn: inviteApi.sendInvite,
    onSuccess: data => {
      if (!data.success) {
        toast.error('Failed to send reminder', {
          description: data.error || 'Please try again',
        });
        return;
      }
      toast.success('Reminder sent successfully');
      handleClose();
    },
    onError: () => {
      toast.error('Failed to send reminder', {
        description: 'Please try again or contact support',
      });
    },
  });

  const handleSendReminder = useCallback(() => {
    if (!canSend || !advisor) return;

    reminderMutation.mutate({
      clientId: reminderConfig.clientId,
      advisorId: advisor.advisorId,
      email: reminderConfig.email.trim(),
      clientFirstName: reminderConfig.firstName.trim(),
      clientLastName: reminderConfig.lastName.trim(),
      advisorProvidedBody: reminderConfig.emailBody,
      intakeFormLink: reminderConfig.intakeFormUrl,
      surveyId: reminderConfig.surveyId,
      createNewClient: false,
    });
  }, [canSend, advisor, reminderConfig, reminderMutation]);

  const openWithClient = useCallback((client: Client) => {
    setReminderConfig({
      email: client.clientEmail || '',
      firstName: client.clientFirstName || '',
      lastName: client.clientLastName || '',
      emailBody: DEFAULT_REMINDER_EMAIL_BODY,
      clientId: client.clientId,
      intakeFormUrl: client.intakeFormUrl || '',
      surveyId: client.surveyId || '',
    });
    setIsOpen(true);
  }, []);

  const isLoading = reminderMutation.isPending;

  const value: SendReminderStoreType = {
    reminderConfig,
    isOpen,
    isLoading,
    isEmailValid,
    canSend,
    setReminderConfig,
    setIsOpen,
    handleConfigChange,
    handleSendReminder,
    handleClose,
    openWithClient,
  };

  return <SendReminderContext.Provider value={value}>{children}</SendReminderContext.Provider>;
}
